import Description from "../../Common/EditForm/Description";
import FieldError from "../../Common/EditForm/FieldError";
import Label from "../../Common/EditForm/Label";
import SimplifiedFormRowProps from "../../Common/EditForm/SimplifiedFormRowProps";
import { appendValidOrInvalidClassName } from "../../Common/EditForm/appendErrorClassNames";
import { forwardRef, useId, useImperativeHandle, useState } from "react";
import { Form } from "react-bootstrap";
import {
  Control,
  FieldValues,
  UseControllerProps,
  useController,
} from "react-hook-form";
import BusinessDocumentItemSelectorDialog from "./BusinessDocumentItemSelectorDialog";
import businessDocumentItemVM from "./businessDocumentItemVM";

/** Methods exposed by the selector. */
export type BusinessDocumentItemSelectorFragmentHandle = {
  openSelector: () => void;
};

/** Properties exposed by the selector that doesn't belong to the RFC infrastructure. */
interface BusinessDocumentItemSelectorFragmentOwnProps {
  required?: boolean;
  onItemChanged?: (item: businessDocumentItemVM) => void;
  businessDocumentType: number;
  documentNumber: string;
}

/** Combined own properties and the properties required by the RFC infrastructure. */
export type BusinessDocumentItemSelectorFragmentProps<T extends FieldValues> =
  SimplifiedFormRowProps &
    BusinessDocumentItemSelectorFragmentOwnProps &
    UseControllerProps<T> & { control: Control<T> };


/** The actual component, but it is actually wrapped by forwardRef so here is the inner part. */
function BusinessDocumentItemSelectorFragmentInner<T extends FieldValues>(
  props: BusinessDocumentItemSelectorFragmentProps<T>,
  ref: React.ForwardedRef<BusinessDocumentItemSelectorFragmentHandle>
) {
  let localProps = { ...props };

  localProps.rules = { ...props.rules };
  localProps.rules.validate = { ...props.rules?.validate };

  if (!!props.required) {
    // we intentionally don't use rules.required, as it has its strong typing
    localProps.rules.validate.valueRequired = (
      value: businessDocumentItemVM | null
    ): string | undefined =>
      value !== null ? undefined : "Pole jest wymagane.";
  }

  const { field, fieldState } = useController(localProps);

  const isTouched = fieldState.isTouched;
  const error = fieldState.error;

  let [isSelectorShown, setSelectorShown] = useState<boolean>(false);

  let [value, setValue] = useState<businessDocumentItemVM | null>(null);

  const lineCodeId = useId();
  const catalogNumberId = useId();

  const onShowSelector = () => {
    setSelectorShown(true);
  };

  // Expose openSelector() to parent via ref
  useImperativeHandle(
    ref,
    () => ({
      openSelector: () => setSelectorShown(true),
    }),
    []
  );

  const onSelectorOk = (businessDocumentItem: businessDocumentItemVM) => {
    setSelectorShown(false);
    setValue(businessDocumentItem);
    field.onChange(businessDocumentItem);
    props.onItemChanged?.(businessDocumentItem);
  };

  return (
    <>
      <div className="row mb-3">
        <Label
          label="Kod pozycji / numer katalogowy *"
          id="lineCode"
          isError={!!error && !!isTouched}
          description="Kod pozycji z faktury VAT lub dokumentu Wz / numer katalogowy towaru. Zostaną uzupełnione automatycznie z wybranej pozycji dokumentu."
        ></Label>

        <div className="col-sm-8">
          <div className="row">
            <div className="col-sm-4 col-xl-4">
              <input
                tabIndex={-1}
                onMouseDown={(e) => e.preventDefault()}
                readOnly={true}
                aria-disabled="true"
                onClick={() => onShowSelector()}
                id={lineCodeId}
                className={appendValidOrInvalidClassName(
                  "form-control -app-fake-disabled",
                  error,
                  isTouched
                )}
                type="text"
                value={field.value?.lineCode || ""}
                name="lineCode"
                placeholder="Wybierz pozycję"
              />
              <FieldError
                error={error?.message as string | null | undefined}
              ></FieldError>
            </div>

            <div className="col-sm-4 col-xl-4">
              <input
                tabIndex={-1}
                onMouseDown={(e) => e.preventDefault()}
                readOnly={true}
                aria-disabled="true"
                onClick={() => onShowSelector()}
                id={catalogNumberId}
                className={appendValidOrInvalidClassName(
                  "form-control -app-fake-disabled",
                  error,
                  isTouched
                )}
                type="text"
                value={field.value?.catalogNumber || ""}
                name="catalogNumber"
                placeholder="Wybierz pozycję"
              />
              <FieldError
                error={error?.message as string | null | undefined}
              ></FieldError>
            </div>
            <div className="col-sm-4 col-xl-4">
              <button
                className="btn btn-success w-100"
                type="button"
                title="Wybierz pozycję z dokumentu"
                onClick={() => onShowSelector()}
              >
                <i className="bi bi-hand-index ps-2 me-2"></i>  Wybierz
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="row mb-3">
        <Label
          label={props.label}
          id={props.id}
          isError={!!error && !!isTouched}
          description="Nazwa towaru. Zostanie uzupełniona automatycznie z wybranej pozycji dokumentu."
        ></Label>

        <div className="col-sm-8">
          <Form.Control
            tabIndex={-1}
            onMouseDown={(e) => e.preventDefault()}
            readOnly={true}
            aria-disabled="true"
            onClick={() => onShowSelector()}
            id={field.name}
            className={appendValidOrInvalidClassName(
              "-app-fake-disabled",
              error,
              isTouched
            )}
            onBlur={field.onBlur}
            value={field.value?.productName || ""}
            name={field.name}
            ref={field.ref}
          />
          <Description description={props.inlineDescription}></Description>
          <FieldError
            error={error?.message as string | null | undefined}
          ></FieldError>
        </div>
      </div>

      <BusinessDocumentItemSelectorDialog
        show={isSelectorShown}
        businessDocumentType={props.businessDocumentType}
        documentNumber={props.documentNumber}
        ok={onSelectorOk}
        cancel={() => {
          setSelectorShown(false);
        }}
      ></BusinessDocumentItemSelectorDialog>
    </>
  );
}

// Wrap with forwardRef, then cast to keep generics on the exported component
export const BusinessDocumentItemSelectorFragment = forwardRef(
  BusinessDocumentItemSelectorFragmentInner
) as <T extends FieldValues>(
  props: BusinessDocumentItemSelectorFragmentProps<T> &
    React.RefAttributes<BusinessDocumentItemSelectorFragmentHandle>
) => React.ReactElement;

export default BusinessDocumentItemSelectorFragment;
